import { useEffect, useState } from 'react';
import Select from '../../components/select/Select.tsx';
import { getUnitPrice } from '../../utils/utils.ts';
import '../product-list/product-list.scss';
import { Product, ProductOptionValues } from '../ProductType.ts';
import { getWeightLabel } from '../utils.ts';
import './product-item.scss';

interface ProductRowProps {
  product: Product;
  handleCartItemChange: (
    id: string,
    purchaseOption: ProductOptionValues,
    quantity: number,
    productPrice: number
  ) => void;
  handleAddToCart: (product: Product) => void;
  inCart?: boolean;
}

const ProductRow = ({
  product,
  handleCartItemChange,
  handleAddToCart,
  inCart = false,
}: ProductRowProps) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [purchaseOption, setSelectedOption] = useState<{
    value: ProductOptionValues;
    label: string;
  } | null>(null);

  const productOptions: { value: ProductOptionValues; label: string }[] = [
    { value: ProductOptionValues.FULL_CRATE, label: 'cajon entero' },
    { value: ProductOptionValues.HALF_CRATE, label: '1/2 cajon' },
    { value: ProductOptionValues.UNIT, label: 'unidad' },
    { value: ProductOptionValues.KG, label: 'kg' },
    { value: ProductOptionValues.GR, label: 'gr' },
    { value: ProductOptionValues.LT, label: 'litros' },
    { value: ProductOptionValues.PACK, label: 'paquete' },
    { value: ProductOptionValues.MAPLE, label: 'maple' },
  ];

  const filteredOptions = (
    typeof product.purchaseOptions === 'string'
      ? product.purchaseOptions
          .split(',')
          .map((opt) => opt.trim().toLowerCase())
      : product.purchaseOptions.map((opt) => opt.toLowerCase())
  )
    .filter((opt) =>
      Object.values(ProductOptionValues).includes(opt as ProductOptionValues)
    )
    .map((opt) => productOptions.find((option) => option.value === opt))
    .filter(
      (opt): opt is { value: ProductOptionValues; label: string } =>
        opt !== undefined
    );

  useEffect(() => {
    if (filteredOptions.length > 0 && !purchaseOption) {
      setSelectedOption(filteredOptions[0]);
    }
  }, [filteredOptions, purchaseOption]);

  useEffect(() => {
    if (filteredOptions.length > 0) {
      handleCartItemChange(
        product.id,
        filteredOptions[0].value as ProductOptionValues,
        1,
        filteredOptions[0].value === ProductOptionValues.HALF_CRATE
          ? parseInt(product.price) / 2
          : parseInt(product.price)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionChange = (
    selected: { value: ProductOptionValues; label: string } | null
  ) => {
    if (!selected) return;
    setSelectedOption(selected);

    const productPrice =
      selected.value === ProductOptionValues.HALF_CRATE
        ? parseInt(product.price) / 2
        : parseInt(product.price);

    handleCartItemChange(
      product.id,
      selected.value as ProductOptionValues,
      quantity,
      productPrice
    );
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);

      if (!purchaseOption || !purchaseOption.value) {
        console.error('Purchase option is missing!');
        return;
      }

      handleCartItemChange(
        product.id,
        purchaseOption.value as ProductOptionValues,
        newQuantity,
        purchaseOption.value === ProductOptionValues.HALF_CRATE
          ? parseInt(product.price) / 2
          : parseInt(product.price)
      );
    }
  };

  const outOfStock = product.hasStock === false;

  const unitPrice = getUnitPrice(
    product.price,
    purchaseOption?.value as ProductOptionValues,
    product.productType,
    product.specialPrices
  );

  return (
    <div
      className={`product-card ${outOfStock ? 'out-of-stock' : ''} ${
        inCart ? 'in-cart' : ''
      }`}
    >
      {outOfStock && <div className="stock-badge">Sin stock</div>}
      {!outOfStock && inCart && (
        <div className="cart-badge-card">✓ En carrito</div>
      )}
      <img
        src={product.image}
        alt={product.name}
        className="product-image margin-0"
      />
      <h3 className="product-card-title margin-0">{product.name}</h3>
      <p className="product-price margin-0">
        <span className="price-symbol">$</span>
        {(unitPrice * Number(quantity)).toLocaleString('es-AR')}
      </p>
      <div className="purchaseOption">
        {filteredOptions.length > 1 ? (
          <Select
            value={purchaseOption}
            options={filteredOptions}
            isSearchable={false}
            onChange={handleOptionChange}
            placeholder="opcion"
          />
        ) : (
          <div className="purchaseOption-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="div-quantity-buttons">
        <button
          className="quantity-button"
          onClick={() => handleQuantityChange(quantity - 1)}
          disabled={quantity <= 1}
        >
          -
        </button>
        <span className="quantity-display">{quantity}</span>
        <button
          className="quantity-button"
          onClick={() => handleQuantityChange(quantity + 1)}
        >
          +
        </button>
      </div>

      <label className="product-weight">
        {getWeightLabel(
          product.boxWeight,
          Number(quantity),
          purchaseOption?.value as ProductOptionValues,
          product.productType
        )}
      </label>

      <button
        className="add-to-cart"
        onClick={() => !outOfStock && handleAddToCart(product)}
        disabled={outOfStock}
      >
        {outOfStock ? 'Sin stock' : 'Agregar al carrito'}
      </button>
    </div>
  );
};

export default ProductRow;
